import type { components } from '@primum/api-types';
import { ApiError } from '@/util/fetchClient';

type AskErrorBody = components['schemas']['AskError'];

type AskErrorCode = NonNullable<AskErrorBody['code']>;

/**
 * What the Ask screens choose behavior and copy by. The API's own codes pass
 * through unchanged. `network` means no response arrived, so the request may or
 * may not have landed: it is the only kind a submit is resent for, under the
 * same turn id, because only an idempotent resend is safe when the outcome is
 * unknown. `npi_required` and `forbidden` are the 403s the ability layer
 * normally keeps a reader from reaching.
 */
export type AskErrorKind =
  | AskErrorCode
  | 'invalid'
  | 'npi_required'
  | 'forbidden'
  | 'network'
  | 'unknown';

const API_CODES: ReadonlySet<string> = new Set<AskErrorCode>([
  'NOT_FOUND',
  'CONVERSATION_ADVANCED',
  'TURN_IN_PROGRESS',
  'LIKELY_IDENTIFIER',
  'CONVERSATION_LIMIT_REACHED',
]);

/**
 * A failed `/v2/ask` call. The message is the API's `error` string, which names
 * a rule, never the user's text; it is still kept out of analytics, which carry
 * `kind` and `status` only.
 *
 * `latestTurnId` is what a `CONVERSATION_ADVANCED` refusal names as the turn to
 * build on, so the stale-parent repair needs no extra read. `undefined` means
 * the response did not say, and `null` that the conversation has no completed
 * turn yet.
 */
export class AskError extends Error {
  constructor(
    public kind: AskErrorKind,
    public status: number | undefined,
    message: string,
    public latestTurnId: string | null | undefined = undefined
  ) {
    super(message);

    this.name = 'AskError';
  }
}

function readField(body: unknown, field: string): unknown {
  return typeof body === 'object' && body !== null && field in body
    ? (body as Record<string, unknown>)[field]
    : undefined;
}

function readString(body: unknown, field: string): string | undefined {
  const value = readField(body, field);

  return typeof value === 'string' ? value : undefined;
}

function kindFor(status: number, code: string | undefined): AskErrorKind {
  if (code && API_CODES.has(code)) {
    return code as AskErrorCode;
  }

  switch (status) {
    case 400:
      return 'invalid';
    case 403:
      return code === 'NPI_REQUIRED' ? 'npi_required' : 'forbidden';
    case 404:
      return 'NOT_FOUND';
    default:
      return 'unknown';
  }
}

/**
 * Maps anything a `fetchClient` call can throw to an `AskError`. A `TypeError`
 * is what `fetch` rejects with when no response arrives, which is the one case
 * treated as `network`.
 */
export function toAskError(error: unknown): AskError {
  if (error instanceof AskError) {
    return error;
  }

  if (error instanceof ApiError) {
    const latestTurnId = readField(error.body, 'latestTurnId');

    return new AskError(
      kindFor(error.status, readString(error.body, 'code')),
      error.status,
      error.message,
      typeof latestTurnId === 'string' || latestTurnId === null ? latestTurnId : undefined
    );
  }

  if (error instanceof TypeError) {
    return new AskError('network', undefined, error.message);
  }

  return new AskError(
    'unknown',
    undefined,
    error instanceof Error ? error.message : 'Unknown error'
  );
}
